import { upperFirst } from 'lodash-es';

import { ArrayElement, returnNonNull } from 'src/common/helpers/ArraysHelper';
import {
	ENVIRONMENT_FILTER_NAME,
	FLOW_FILTER_NAME
} from 'src/common/helpers/GQLRequestsHelper/labelFilters';
import { normalizeCustomField } from 'src/common/helpers/GQLRequestsHelper/QueryResultsTransformHelper';

import { IWebSdkTest, TWebSdkTestsResult } from '../../../../types/WebSdkDashboardServiceTypes';
import { TestsQuery } from '../Tests.generated';

type Test = NonNullable<ArrayElement<NonNullable<TestsQuery['wsdk_test_runs']>>>;

const formatFullLabel = (prefix: string, value: string): string =>
	`${prefix}: ${upperFirst(value.trim())}`;

const getAllLabelValues = (test: Test): string[] => [
	...test.environments
		.filter((env) => !!env && env.trim().length > 0)
		.map((env) => formatFullLabel(ENVIRONMENT_FILTER_NAME, env)),
	...test.flows
		.filter((flow) => !!flow && flow.trim().length > 0)
		.map((flow) => formatFullLabel(FLOW_FILTER_NAME, flow)),
	...(test.custom_fields
		?.map(normalizeCustomField)
		?.filter(returnNonNull)
		?.map(([labelName, labelValue]) => `${labelName}: ${labelValue}`) ?? [])
];

export const transformTestsResults = (rawResults: TestsQuery): TWebSdkTestsResult => {
	const rows: IWebSdkTest[] =
		rawResults.wsdk_test_runs?.filter(returnNonNull).map((test) => ({
			runId: test.run_id,
			totalIssues: test.total_issues,
			criticalIssues: test.critical_issues,
			runTime: test.run_time,
			testName: test.test_name,
			testPath: test.test_path,
			propertyIds: test.property_ids,
			labels: getAllLabelValues(test),
			environments: test.environments ?? [],
			flows: test.flows ?? []
		})) ?? [];

	let total = 0;
	if (rawResults.total?.length) {
		total = rawResults.total[0]?.run_id_count ?? 0;
	}

	let unfilteredTotal = 0;
	if (rawResults.unfiltered_total?.length) {
		unfilteredTotal = rawResults.unfiltered_total[0]?.run_id_count ?? 0;
	}

	return {
		rows,
		total,
		unfilteredTotal
	};
};
